import { Divider, Progress, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../general/table-components/table";
import {
  IAreaClientStat,
  IAreaClientStats,
} from "../../../models/statistcs/respons";
import styles from "./style.module.scss";

interface IProps {
  stats?: IAreaClientStats;
}

interface IAreaClientRow extends IAreaClientStat {
  key: string;
  isOutside: boolean;
  clientSharePercent: number;
  realClientRatePercent: number;
}

const getPercent = (value: number, total: number) =>
  total > 0 ? (value / total) * 100 : 0;

const AreaClientStats: FC<IProps> = ({ stats }) => {
  const { t, i18n } = useTranslation();

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language]
  );

  const rows = useMemo<IAreaClientRow[]>(() => {
    if (!stats) return [];

    const totalClientCount = stats.totalClientCount || 0;

    const buildRow = (
      item: IAreaClientStat,
      index: number,
      isOutside = false
    ): IAreaClientRow => ({
      ...item,
      key: isOutside ? "outside-areas" : `area-${item.areaId ?? index}`,
      isOutside,
      clientSharePercent: getPercent(item.clientCount || 0, totalClientCount),
      realClientRatePercent: getPercent(
        item.realClientCount || 0,
        item.clientCount || 0
      ),
    });

    return [
      ...(stats.areas || []).map((area, index) => buildRow(area, index)),
      ...(stats.outsideAreas
        ? [buildRow(stats.outsideAreas, -1, true)]
        : []),
    ];
  }, [stats]);

  if (!stats || rows.length === 0) return null;

  const formatNumber = (value?: number | null) =>
    numberFormatter.format(value ?? 0);

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  const renderPercent = (value: number) => {
    const percent = Math.min(100, Math.max(0, Number(value.toFixed(1))));

    return (
      <div className={styles.percentCell}>
        <Progress percent={percent} showInfo={false} size="small" />
        <span>{formatPercent(value)}</span>
      </div>
    );
  };

  const columns: ColumnsType<IAreaClientRow> = [
    {
      title: t("area"),
      dataIndex: "areaName",
      key: "areaName",
      ellipsis: true,
      render: (name: string, row) => (
        <div className={styles.areaName}>
          <span>{row.isOutside ? t("outside_areas") : name}</span>
          {row.isOutside && (
            <Tooltip title={t("outside_areas_tooltip")}>
              <Tag color="blue">{t("outside_areas")}</Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t("clients"),
      dataIndex: "clientCount",
      key: "clientCount",
      width: 130,
      sorter: (a, b) => a.clientCount - b.clientCount,
      render: (value: number) => (
        <span className={styles.count}>{formatNumber(value)}</span>
      ),
    },
    {
      title: t("real_clients"),
      dataIndex: "realClientCount",
      key: "realClientCount",
      width: 150,
      sorter: (a, b) => a.realClientCount - b.realClientCount,
      render: (value: number) => (
        <span className={styles.count}>{formatNumber(value)}</span>
      ),
    },
    {
      title: t("client_share"),
      dataIndex: "clientSharePercent",
      key: "clientSharePercent",
      width: 180,
      sorter: (a, b) => a.clientSharePercent - b.clientSharePercent,
      render: renderPercent,
    },
    {
      title: t("real_client_rate"),
      dataIndex: "realClientRatePercent",
      key: "realClientRatePercent",
      width: 180,
      sorter: (a, b) => a.realClientRatePercent - b.realClientRatePercent,
      render: renderPercent,
    },
  ];

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t("area_client_stats")}</Divider>
      <div className={styles.tablePanel}>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowClassName={(row) => (row.isOutside ? styles.outsideRow : "")}
          scroll={{ x: 760 }}
        />
      </div>
    </div>
  );
};

export default AreaClientStats;
